import { useState, ChangeEvent } from 'react';
import { login } from '../../../../services/Users';
import { parseClaimsFromHeader } from '../../../../utils/auth/ClaimsParser';
import { IsValidEmail, IsValidName, IsValidPassword } from '../../../../utils/Validators';

export interface AuthFormData {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    newPassword: string;
    confirmNewPassword: string;
    verificationCode: string;
    mfaVerificationCode: string;
    isValid: boolean;
}

export interface LoginCallbacks {
    onMfaSetup?: (mfaData: any) => void;
    onMfaVerify?: (mfaData: any) => void;
    onError?: (error: string) => void;
    onSuccess?: (claims: any) => void;
}

export interface UseAuthFormReturn {
    authFormData: AuthFormData;
    onChangeAuthForm: (e: ChangeEvent<HTMLInputElement>) => void;
    resetAuthForm: () => void;
    handleLogin: (email: string, password: string, callbacks?: LoginCallbacks) => Promise<void>;
    validatePasswordMatch: () => boolean;
    validateNewPasswordMatch: () => boolean;
    validateOTPMatch: () => boolean;
}

export const UseAuthForm = (
    setAuthClaims: (claims: any) => void,
    csrfToken: string | null,
    navigate: any,
    flowType: 'login' | 'signup' | 'forgotPassword' | 'confirm' = 'login'
): UseAuthFormReturn => {
    const [authFormData, setAuthFormData] = useState<AuthFormData>({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        verificationCode: '',
        mfaVerificationCode: '',
        isValid: false
    });

    /**
     * Centralized login function
     * Handles login with MFA setup/verification flow
     */
    const handleLogin = (email: string, password: string, callbacks: LoginCallbacks = {}): Promise<void> => {
        const { onMfaSetup, onMfaVerify, onError, onSuccess } = callbacks;

        return login(email, password, csrfToken).then((response) => {
            if (response.data.error) {
                if (response.data.error === "MFA_SETUP") {
                    // Check for claims in response data even when MFA_SETUP is required
                    if (response.data.claims && Object.keys(response.data.claims).length > 0) {
                        const claimsWithEmail = { ...response.data.claims, email };
                        setAuthClaims(claimsWithEmail);
                        if (onSuccess) {
                            onSuccess(claimsWithEmail);
                        } else {
                            navigate('/dashboard');
                        }
                    } else if (onMfaSetup) {
                        onMfaSetup(response.data.mfa);
                    }
                } else if (response.data.error === "SOFTWARE_TOKEN_MFA" || response.data.error.includes("SOFTWARE_TOKEN_MFA")) {
                    // Check for claims in response data even when MFA verification is required
                    if (response.data.claims && Object.keys(response.data.claims).length > 0) {
                        const claimsWithEmail = { ...response.data.claims, email };
                        setAuthClaims(claimsWithEmail);
                        if (onSuccess) {
                            onSuccess(claimsWithEmail);
                        } else {
                            navigate('/dashboard');
                        }
                    } else if (onMfaVerify) {
                        onMfaVerify(response.data.mfa);
                    }
                } else {
                    if (onError) {
                        onError(response.data.error || "An unexpected error occurred. Please try again.");
                    }
                }
            } else {
                resetAuthForm();
                const claims = parseClaimsFromHeader(response);
                if (claims) {
                    const claimsWithEmail = { ...claims, email };
                    setAuthClaims(claimsWithEmail);
                    if (onSuccess) {
                        onSuccess(claimsWithEmail);
                    } else {
                        navigate('/dashboard');
                    }
                }
            }
        }).catch((err: any) => {
            console.error('Login error:', err);
            if (onError) {
                // Extract error message from backend response
                // Backend format: {"code":401,"msg":"invalid credentials"}
                const errorMessage = err.response?.data?.msg ||
                                   err.response?.data?.error ||
                                   err.response?.data?.message ||
                                   err.message ||
                                   "Network error. Please check your connection and try again.";
                onError(errorMessage);
            }
        });
    };

    const onChangeAuthForm = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setAuthFormData((prev) => {
            const newData = {
                ...prev,
                [name]: value,
            };

            // Recompute validation holistically from the latest data to avoid order-dependence
            let isValid = false;
            if (flowType === 'login') {
                isValid = IsValidEmail(newData.email) && IsValidPassword(newData.password);
            } else if (flowType === 'signup') {
                isValid = IsValidName(newData.name) && IsValidEmail(newData.email) && IsValidPassword(newData.password);
            } else if (flowType === 'forgotPassword') {
                // Email step
                isValid = IsValidEmail(newData.email);
                // New password step
                if (IsValidPassword(newData.newPassword) && newData.confirmNewPassword.length > 0) {
                    isValid = isValid && (newData.newPassword === newData.confirmNewPassword);
                }
                // OTP step
                if (newData.verificationCode) {
                    isValid = isValid && newData.verificationCode.length === 6;
                }
            } else if (flowType === 'confirm') {
                // For confirm flow, validate email and verification code
                isValid = IsValidEmail(newData.email) && newData.verificationCode.length === 6;
            } else {
                // Default safe behavior
                isValid = prev.isValid;
            }

            return {
                ...newData,
                isValid
            };
        });
    };

    const resetAuthForm = () => {
        setAuthFormData({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            newPassword: '',
            confirmNewPassword: '',
            verificationCode: '',
            mfaVerificationCode: '',
            isValid: false
        });
    };

    // Validation functions
    const validatePasswordMatch = (): boolean => {
        return authFormData.password === authFormData.confirmPassword;
    };

    const validateNewPasswordMatch = (): boolean => {
        return authFormData.newPassword === authFormData.confirmNewPassword;
    };

    const validateOTPMatch = (): boolean => {
        // For now, OTP codes don't need matching validation
        // This could be extended if needed for future features
        return true;
    };

    return {
        authFormData,
        onChangeAuthForm,
        resetAuthForm,
        handleLogin,
        validatePasswordMatch,
        validateNewPasswordMatch,
        validateOTPMatch
    };
};
